import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { CartFab, CartDrawer } from '../components/Cart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QRCategory = ({ restaurantIdOverride, slugOverride, tableNumberOverride, categoryIdOverride }) => {
  const params = useParams();
  const restaurantId = params.restaurantId || restaurantIdOverride;
  const categoryId = params.categoryId || categoryIdOverride;
  const tableNumber = params.tableNumber || tableNumberOverride;
  const navigate = useNavigate();
  const { t, lang, setLang, getLocalized } = useLanguage();
  const cart = useCart();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(false);

  const basePath = React.useMemo(() => {
    if (slugOverride) {
      return tableNumber ? `/${slugOverride}/masa${tableNumber}` : `/${slugOverride}`;
    }
    return tableNumber ? `/qr-menu/${restaurantId}/t/${tableNumber}` : `/qr-menu/${restaurantId}`;
  }, [slugOverride, tableNumber, restaurantId]);

  useEffect(() => {
    fetchData();
  }, [restaurantId, categoryId, lang]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, allCatRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/menu/qr/${restaurantId}/category/${categoryId}?lang=${lang}`),
        axios.get(`${BACKEND_URL}/api/menu/categories/${restaurantId}`),
      ]);
      setData(catRes.data);
      setCategories(allCatRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <p className="text-red-600 text-xl">Kateqoriya tapılmadı</p>
      </div>
    );
  }

  const { restaurant, category, items } = data;
  const currencySymbol = restaurant.currency_symbol || '₼';

  const langOptions = [
    { code: 'az', label: 'Azərbaycan', flag: 'AZ' },
    { code: 'tr', label: 'Türkçe', flag: 'TR' },
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'ru', label: 'Русский', flag: 'RU' },
  ];
  const currentLang = langOptions.find((l) => l.code === lang);

  return (
    <div className="min-h-screen bg-[#2B170D]">
      {/* Compact header */}
      <div
        className="relative h-24 md:h-28 bg-cover bg-center"
        style={{
          backgroundImage: restaurant.cover_url
            ? `url(${restaurant.cover_url})`
            : 'linear-gradient(135deg, #1a0e08 0%, #3d2418 50%, #1a0e08 100%)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
          <Link to={basePath} className="flex items-center space-x-3">
            {restaurant.logo_url ? (
              <img
                src={restaurant.logo_url}
                alt={restaurant.name}
                className="w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-white object-cover"
              />
            ) : (
              <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white">
                {restaurant.name?.[0]}
              </div>
            )}
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white">{restaurant.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                {tableNumber && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow" data-testid="table-badge">
                    🪑 Masa #{tableNumber}
                  </span>
                )}
                {restaurant.service_fee_percentage > 0 && (
                  <p className="text-xs text-amber-200">
                    {t('service_fee').toUpperCase()}: {restaurant.service_fee_percentage}%
                  </p>
                )}
              </div>
            </div>
          </Link>

          <div className="relative">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full px-3 py-2 transition"
            >
              <span className="text-white text-sm font-bold">{currentLang?.flag}</span>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showLangMenu && (
              <div className="absolute right-0 top-12 bg-white rounded-xl shadow-2xl py-2 min-w-[160px] z-50">
                {langOptions.map((opt) => (
                  <button
                    key={opt.code}
                    onClick={() => {
                      setLang(opt.code);
                      setShowLangMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition flex items-center space-x-3 ${
                      lang === opt.code ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-sm font-bold w-8">{opt.flag}</span>
                    <span className="text-sm">{opt.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category slider */}
      <div className="bg-[#2B170D] border-b border-white/10 shadow-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center space-x-3 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setShowSideMenu(true)}
              className="flex items-center space-x-2 bg-[#5C3A24] hover:bg-[#6E4730] rounded-full px-4 py-2 shadow-sm flex-shrink-0 transition-colors"
              data-testid="sidemenu-toggle"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-sm font-medium text-white">{t('menu')}</span>
            </button>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`${basePath}/category/${cat.id}`}
                className={`flex items-center space-x-2 rounded-full pl-1 pr-4 py-1 transition flex-shrink-0 ${
                  cat.id === categoryId
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-[#4A2C1A] hover:bg-[#5C3A24] text-white'
                }`}
              >
                {cat.image_url ? (
                  <img
                    src={cat.image_url}
                    alt={getLocalized(cat, 'name')}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    cat.id === categoryId
                      ? 'bg-emerald-700 text-white'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {getLocalized(cat, 'name')?.[0]}
                  </div>
                )}
                <span className="text-sm font-medium whitespace-nowrap">
                  {getLocalized(cat, 'name')}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Items grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white inline-block" data-testid="category-heading">
            {getLocalized(category, 'name')}
            <span className="block w-12 h-1 bg-emerald-500 rounded mt-2"></span>
          </h2>
          {getLocalized(category, 'description') && (
            <p className="text-stone-300 mt-2">{getLocalized(category, 'description')}</p>
          )}
        </div>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">{t('no_items')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((item) => {
              const cartItem = cart.items.find((i) => i.item_id === item.id);
              const qty = cartItem?.quantity || 0;
              return (
              <div
                key={item.id}
                className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group flex flex-col"
                data-testid={`menu-item-${item.id}`}
              >
                <Link to={`${basePath}/item/${item.id}`} className="block">
                <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={getLocalized(item, 'name')}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400 text-6xl">
                      🍽️
                    </div>
                  )}
                  {/* Price badge */}
                  <div className="absolute bottom-3 right-3 bg-stone-900/80 backdrop-blur-sm text-white font-bold px-3 py-1.5 rounded-full text-sm" data-testid={`item-price-${item.id}`}>
                    {item.discount_price ? (
                      <>
                        <span className="line-through text-stone-400 text-xs mr-1">{item.price}</span>
                        {item.discount_price} {currencySymbol}
                      </>
                    ) : (
                      <>{item.price} {currencySymbol}</>
                    )}
                  </div>
                  {item.is_featured && (
                    <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      ★ {t('featured')}
                    </div>
                  )}
                </div>
                <div className="p-4 pb-2">
                  <h3 className="font-bold text-gray-900 text-base md:text-lg leading-tight">
                    {getLocalized(item, 'name')}
                  </h3>
                  {getLocalized(item, 'description') && (
                    <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                      {getLocalized(item, 'description')}
                    </p>
                  )}
                </div>
                </Link>
                {/* ADD TO CART CONTROL */}
                <div className="px-4 pb-4 pt-1 mt-auto">
                  {qty === 0 ? (
                    <button
                      onClick={() => cart.addItem(item, 1)}
                      className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition flex items-center justify-center gap-1.5"
                      data-testid={`add-to-cart-${item.id}`}
                    >
                      <span>＋</span> Səbətə əlavə et
                    </button>
                  ) : (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl p-1">
                      <button
                        onClick={() => cart.updateQuantity(item.id, -1)}
                        className="w-9 h-9 rounded-lg bg-white hover:bg-stone-100 text-stone-700 font-bold"
                        data-testid={`qty-minus-${item.id}`}
                      >−</button>
                      <span className="font-bold text-emerald-700" data-testid={`qty-display-${item.id}`}>{qty}</span>
                      <button
                        onClick={() => cart.addItem(item, 1)}
                        className="w-9 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                        data-testid={`qty-plus-${item.id}`}
                      >＋</button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SIDE MENU DRAWER */}
      {showSideMenu && (
        <div className="fixed inset-0 z-[60]" data-testid="side-menu-drawer">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowSideMenu(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto animate-slide-in-left">
            <div
              className="relative px-5 py-6 bg-cover bg-center"
              style={{
                backgroundImage: restaurant.cover_url
                  ? `url(${restaurant.cover_url})`
                  : 'linear-gradient(135deg, #1a0e08 0%, #3d2418 50%, #1a0e08 100%)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/80"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {restaurant.logo_url ? (
                    <img src={restaurant.logo_url} alt={restaurant.name} className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white">
                      {restaurant.name?.[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="text-white font-bold text-lg">{restaurant.name}</h3>
                    {restaurant.service_fee_percentage > 0 && (
                      <p className="text-xs text-amber-200">{t('service_fee')}: {restaurant.service_fee_percentage}%</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowSideMenu(false)}
                  className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                  data-testid="side-menu-close"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4">
              <Link
                to={basePath}
                onClick={() => setShowSideMenu(false)}
                className="flex items-center space-x-3 p-2.5 mb-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-emerald-700">{t('our_menu')}</p>
                  <p className="text-xs text-emerald-600">{t('back')}</p>
                </div>
              </Link>

              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3 mt-4 px-2">
                {t('menu')}
              </h4>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`${basePath}/category/${cat.id}`}
                    onClick={() => setShowSideMenu(false)}
                    className={`flex items-center space-x-3 p-2.5 rounded-xl transition group ${
                      cat.id === categoryId
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'hover:bg-emerald-50'
                    }`}
                    data-testid={`side-menu-cat-${cat.id}`}
                  >
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={getLocalized(cat, 'name')} className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700 font-bold text-lg">
                        {getLocalized(cat, 'name')?.[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold transition ${cat.id === categoryId ? 'text-emerald-800' : 'text-gray-800 group-hover:text-emerald-700'}`}>
                        {getLocalized(cat, 'name')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {cat.item_count || 0} {t('products')}
                      </p>
                    </div>
                    <svg className={`w-4 h-4 transition ${cat.id === categoryId ? 'text-emerald-600' : 'text-gray-400 group-hover:text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 px-2 text-sm text-gray-600 space-y-1">
                <p>📍 {restaurant.address}</p>
                <p>📞 {restaurant.phone}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <CartFab lang={lang} />
      <CartDrawer restaurant={restaurant} lang={lang} />
    </div>
  );
};
